import React, { useEffect, useState } from "react";
import { Loader2, FileText, Image as ImageIcon, GraduationCap, ChevronDown, ChevronUp, ShieldCheck, Mail, UserCheck } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import api, { API } from "@/lib/api";
import { toast } from "sonner";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-AU", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");
const LABELS = { photo: "Profile photo", clearance: "WWCC / police check", transcript: "Qualifications" };

const Row = ({ label, value }) => (value ? (
  <div className="flex gap-3 text-sm py-1.5 border-b border-slate-100 last:border-0">
    <span className="text-slate-500 w-36 shrink-0">{label}</span>
    <span className="text-slate-900 break-words">{value}</span>
  </div>
) : null);

function Application({ app, token, onPublish }) {
  const [open, setOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const files = app.files || {};
  const publish = async () => {
    setDrafting(true);
    try {
      const r = await api.get(`/admin/tutor-applications/${app.application_id}/draft`);
      onPublish(r.data);
    } catch {
      toast.error("Could not prepare that profile. Please try again.");
    } finally {
      setDrafting(false);
    }
  };
  const resend = async () => {
    setSending(true);
    try {
      const r = await api.post(`/admin/tutor-applications/${app.application_id}/resend`);
      toast.success(`Emailed to ${r.data.to.join(", ")} with ${r.data.attached} attachment${r.data.attached === 1 ? "" : "s"}`);
    } catch {
      toast.error("Could not send that email. Please try again.");
    } finally {
      setSending(false);
    }
  };
  const fileUrl = (f) => (f.token ? `${API}/tutor-files/${f.file_id}?t=${f.token}` : `${API}/admin/tutor-files/${f.file_id}${token ? `?auth=${encodeURIComponent(token)}` : ""}`);
  return (
    <Card className="border-slate-200 overflow-hidden" data-testid={`tutor-app-${app.application_id}`}>
      <div className="p-4 flex items-start gap-4">
        {files.photo ? (
          <img src={fileUrl(files.photo)} alt={app.name} loading="lazy"
               className="w-16 h-16 rounded-full object-cover border border-slate-200 shrink-0"
               data-testid={`tutor-app-photo-${app.application_id}`} />
        ) : (
          <span className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
            <ImageIcon className="w-5 h-5 text-slate-400" />
          </span>
        )}
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-slate-900">{app.name}</p>
          <p className="text-sm text-slate-600 break-words">
            <a href={`mailto:${app.email}`} className="text-teal-700 hover:underline">{app.email}</a>
            {app.phone ? ` · ${app.phone}` : ""}{app.location ? ` · ${app.location}` : ""}
          </p>
          <p className="text-sm text-slate-500 mt-1">{app.subjects || "—"}</p>
          <div className="flex flex-wrap gap-1.5 mt-2">
            {app.check_type && <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100 text-[10px]"><ShieldCheck className="w-3 h-3 mr-1" />{app.check_type}</Badge>}
            {Object.keys(files).map((k) => <Badge key={k} variant="secondary" className="text-[10px]">{LABELS[k] || k}</Badge>)}
            {app.marketing_opt_in && <Badge variant="secondary" className="text-[10px]">Opted in</Badge>}
          </div>
        </div>
        <div className="text-right shrink-0">
          <p className="text-xs text-slate-400 whitespace-nowrap">{fmt(app.created_at)}</p>
          <Button size="sm" variant="outline" className="rounded-full mt-2" onClick={() => setOpen((v) => !v)} data-testid={`tutor-app-toggle-${app.application_id}`}>
            {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />} {open ? "Hide" : "View all"}
          </Button>
          <Button size="sm" variant="outline" className="rounded-full mt-2 block w-full" disabled={sending} onClick={resend} data-testid={`tutor-app-email-${app.application_id}`}>
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />} Email me this
          </Button>
          <Button size="sm" className="rounded-full mt-2 block w-full bg-emerald-600 hover:bg-emerald-700" disabled={drafting} onClick={publish} data-testid={`tutor-app-publish-${app.application_id}`}>
            {drafting ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserCheck className="w-4 h-4" />} Publish as tutor
          </Button>
        </div>
      </div>
      {open && (
        <div className="border-t border-slate-100 p-4 bg-slate-50 space-y-4" data-testid={`tutor-app-detail-${app.application_id}`}>
          <div>
            <Row label="Year levels" value={app.year_levels} />
            <Row label="Delivery" value={app.mode} />
            <Row label="Hourly rate" value={app.rate} />
            <Row label="Availability" value={app.availability} />
            <Row label="Qualifications" value={app.qualifications} />
            <Row label="Experience" value={app.experience} />
            <Row label="Bio" value={app.about} />
            <Row label="Clearance number" value={app.check_number} />
            <Row label="Clearance expiry" value={app.check_expiry} />
          </div>
          <div className="flex flex-wrap gap-2">
            {Object.entries(files).map(([k, f]) => (
              <Button key={k} asChild size="sm" variant="outline" className="rounded-full" data-testid={`tutor-app-file-${app.application_id}-${k}`}>
                <a href={fileUrl(f)} target="_blank" rel="noreferrer">
                  {k === "photo" ? <ImageIcon className="w-4 h-4" /> : k === "transcript" ? <GraduationCap className="w-4 h-4" /> : <FileText className="w-4 h-4" />}
                  {LABELS[k] || k} <span className="text-slate-400 font-normal">({Math.round((f.size || 0) / 1024)} KB)</span>
                </a>
              </Button>
            ))}
            {Object.keys(files).length === 0 && <p className="text-sm text-slate-500">No files were attached.</p>}
          </div>
        </div>
      )}
    </Card>
  );
}

export default function TutorApplicationsPanel({ onPublish }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  // Session is a same-site cookie, so <img src> and link clicks authenticate themselves.
  const token = "";

  const load = () => {
    setError("");
    api.get("/admin/tutor-applications")
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.status === 401 || e?.response?.status === 403
        ? "Your admin session has expired. Sign in again to see applications."
        : "Could not load applications just now — this is a connection problem, not an empty list. Try again."));
  };
  useEffect(() => { load(); }, []);

  if (error) {
    return (
      <div className="space-y-3" data-testid="tutor-apps-error">
        <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-xl p-4">{error}</p>
        <Button size="sm" variant="outline" className="rounded-full" onClick={load} data-testid="tutor-apps-retry">Try again</Button>
      </div>
    );
  }
  if (!data) return <Loader2 className="w-5 h-5 animate-spin text-teal-600" data-testid="tutor-apps-loading" />;
  if (data.count === 0) return <p className="text-sm text-slate-500" data-testid="tutor-apps-empty">No tutor applications yet.</p>;

  return (
    <div className="space-y-3" data-testid="tutor-apps-panel">
      <p className="text-sm text-slate-500">{data.count} application{data.count === 1 ? "" : "s"} · photos and documents are private and only visible to you.</p>
      {data.applications.map((a) => <Application key={a.application_id || a.id} app={a} token={token} onPublish={onPublish} />)}
    </div>
  );
}
